import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Box,
  Chip,
  IconButton,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CategoryIcon from '@mui/icons-material/Category';
import api from '../services/api';

const Favorites = () => {
  const [products, setProducts] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadFavorites();
  }, []);

  const loadFavorites = async () => {
    try {
      const response = await api.get('/favorites');
      setProducts(response.data);
    } catch (error) {
      console.error('Erro ao carregar favoritos:', error);
    }
  };

  const removeFavorite = async (productId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/favorites/${productId}`);
      setProducts(products.filter(p => p.id !== productId));
    } catch (error) {
      console.error('Erro ao remover favorito:', error);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 6, pt: 10 }}>
      <Typography 
        variant="h3" 
        component="h1" 
        gutterBottom
        sx={{
          fontFamily: '"Plus Jakarta Sans", sans-serif',
          fontWeight: 700,
          mb: 4,
        }}
      >
        Meus Favoritos
      </Typography>

      {products.length === 0 ? (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h6" color="text.secondary">
            Você ainda não tem produtos favoritados
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {products.map((product) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
              <Card
                sx={{ 
                  cursor: 'pointer', 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                  },
                }}
                onClick={() => navigate(`/product/${product.id}`)}
              >
                <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                  {product.image_url ? (
                    <CardMedia
                      component="img"
                      height="280"
                      image={product.image_url}
                      alt={product.name}
                      sx={{
                        objectFit: 'cover',
                        transition: 'transform 0.5s ease',
                        '&:hover': {
                          transform: 'scale(1.05)',
                        },
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 280,
                        backgroundColor: 'background.default',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CategoryIcon sx={{ fontSize: 60, color: 'primary.main' }} />
                    </Box>
                  )}
                  <IconButton
                    onClick={(e) => removeFavorite(product.id, e)}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: '#ffffff',
                        color: 'primary.dark',
                      },
                    }}
                  >
                    <FavoriteIcon />
                  </IconButton>
                </Box>
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Typography 
                    variant="h6" 
                    component="h2"
                    sx={{ 
                      flexGrow: 1,
                      fontFamily: '"Plus Jakarta Sans", sans-serif',
                      fontWeight: 600,
                      mb: 1,
                      minHeight: '3em',
                    }}
                  >
                    {product.name}
                  </Typography>
                  <Chip 
                    label={product.category} 
                    size="small" 
                    sx={{ 
                      mb: 1.5,
                      backgroundColor: 'background.default',
                      color: 'primary.main',
                      fontWeight: 500,
                    }} 
                  />
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mt: 1,
                      mb: 2,
                      minHeight: '3em',
                      lineHeight: 1.6,
                    }}
                  >
                    {product.description?.substring(0, 120) || 'Produto exclusivo de alto padrão.'}
                    {product.description && product.description.length > 120 && '...'}
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    pt: 2,
                    borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                  }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 500,
                        color: 'primary.main',
                        letterSpacing: '0.5px',
                      }}
                    >
                      {product.store_name}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default Favorites;
